'use client';

import { useRef } from 'react';
import { useSectionViewTracking } from '@/hooks/use-section-view-tracking';

export interface SectionTrackerProps {
  /** Name of the section, e.g. 'hero', 'features', 'final_cta'. */
  section: string;
  /** Name of the page the section lives on, e.g. 'landing', 'pricing'. */
  page: string;
}

/**
 * Invisible marker that fires a `section_viewed` event the first time it
 * scrolls into view. Drop this at the top of any existing section to measure
 * funnel depth without restructuring the page.
 *
 * @example
 * ```tsx
 * <section aria-labelledby="hero-heading">
 *   <SectionTracker section="hero" page="landing" />
 *   …
 * </section>
 * ```
 */
export function SectionTracker({ section, page }: SectionTrackerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  useSectionViewTracking(ref, section, page);
  return <span ref={ref} aria-hidden="true" className="sr-only" />;
}
